/**
 * Merchant_checkout.jsx
 * ESDRAS MERCHANT MARKETPLACE — Checkout Page
 * Route: /merchant/checkout
 * Features:
 *   - Guest email verification via backend link (expires, redirects back with params)
 *   - Delivery address detection: Lagos = ₦2,500 | Outside Lagos = ₦10,000
 *   - Delivery confirmation modal before adding to total
 *   - Comment box for buyer notes
 *   - Payment method selection: Monnify / Stripe / Esdras Wallet
 *   - On-screen receipt + PDF download
 *   - Guest buyer data logged to Firestore (exportable)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  doc, getDoc, setDoc, addDoc, collection, serverTimestamp, updateDoc, increment,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../../firebase';
import { toast } from 'react-hot-toast';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const GOLD               = '#B8860B';
const DEEP_BLACK         = '#050a0f';
const CARD_BG            = '#0a0f14';
const BORDER             = '#1a2631';
const DELIVERY_LAGOS     = 2500;
const DELIVERY_OUTSIDE   = 10000;
const SAFE_IMG           = 'https://placehold.co/120x120/050a0f/B8860B?text=Product';

// Lagos keyword detection
const LAGOS_KEYWORDS = ['lagos', 'lekki', 'victoria island', 'vi', 'ikoyi', 'surulere',
  'yaba', 'ikeja', 'maryland', 'ojota', 'berger', 'agege', 'mushin', 'oshodi',
  'ajah', 'sangotedo', 'ibeju', 'badagry', 'epe', 'ikorodu', 'apapa', 'festac'];

function isLagosAddress(addr) {
  if (!addr) return false;
  const lower = addr.toLowerCase();
  return LAGOS_KEYWORDS.some(kw => lower.includes(kw));
}

// ─── CHECKOUT STEPS ──────────────────────────────────────────────────────────
const STEPS = { DETAILS: 0, DELIVERY: 1, PAYMENT: 2, RECEIPT: 3 };

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function Merchant_checkout() {
  const location    = useLocation();
  const navigate    = useNavigate();
  const [searchParams] = useSearchParams();
  const functions   = getFunctions();

  // From navigation state (product page → checkout)
  const navState    = location.state || {};
  const [product,   setProduct]   = useState(navState.product   || null);
  const [qty,       setQty]       = useState(navState.qty       || 1);
  const [productId, setProductId] = useState(navState.productId || searchParams.get('productId') || '');
  const [merchantId, setMerchantId] = useState(navState.merchantId || '');

  // Auth
  const [user,          setUser]          = useState(null);
  const [userWalletBal, setUserWalletBal] = useState(0);

  // Guest verification flow
  const [guestVerified, setGuestVerified] = useState(false);
  const [sessionRef,    setSessionRef]    = useState(searchParams.get('session') || '');
  const isVerifiedReturn = searchParams.get('verified') === 'true' && !!searchParams.get('session');

  // Buyer details (guest or auto-filled for logged-in user)
  const [buyer, setBuyer] = useState({
    name:    '',
    email:   '',
    phone:   '',
    address: '',
    comment: '',
  });

  // Delivery
  const [deliveryFee,         setDeliveryFee]         = useState(null); // null = not yet confirmed
  const [deliveryConfirmed,   setDeliveryConfirmed]   = useState(false);
  const [showDeliveryModal,   setShowDeliveryModal]   = useState(false);
  const [deliveryZone,        setDeliveryZone]        = useState(null); // 'lagos' | 'outside'

  // Payment
  const [paymentMethod, setPaymentMethod] = useState('monnify'); // 'monnify' | 'stripe' | 'wallet'
  const [step,          setStep]          = useState(STEPS.DETAILS);
  const [loading,       setLoading]       = useState(false);
  const [loadingMsg,    setLoadingMsg]    = useState('');

  // Verification email send state
  const [verifyEmailSent,    setVerifyEmailSent]    = useState(false);
  const [verifyEmailLoading, setVerifyEmailLoading] = useState(false);

  // Receipt
  const [orderData, setOrderData] = useState(null);
  const receiptRef = useRef(null);

  // ── Load product if not passed via state ─────────────────────────────────
  useEffect(() => {
    if (!product && productId) {
      getDoc(doc(db, 'merchant_products', productId)).then(snap => {
        if (snap.exists()) {
          setProduct({ id: snap.id, ...snap.data() });
          setMerchantId(snap.data().merchantId);
        }
      });
    }
  }, [productId, product]);

  // ── Auth observer + pre-fill buyer ───────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const uSnap = await getDoc(doc(db, 'users', u.uid)).catch(() => null);
        const uData = uSnap?.data() || {};
        setBuyer(prev => ({
          ...prev,
          name:  uData.fullName  || u.displayName || '',
          email: u.email         || '',
          phone: uData.phoneNumber || '',
        }));
        setUserWalletBal(uData.walletBalance || 0);
        setGuestVerified(true); // logged-in users are already verified
      }
    });
    return () => unsub();
  }, []);

  // ── Handle verified return from email link ────────────────────────────────
  useEffect(() => {
    if (isVerifiedReturn && sessionRef) {
      const loadSession = async () => {
        const snap = await getDoc(doc(db, 'guest_checkout_sessions', sessionRef)).catch(() => null);
        if (snap?.exists()) {
          const d = snap.data();
          if (d.expiresAt?.toDate && d.expiresAt.toDate() < new Date()) {
            toast.error('Verification link expired. Please try again.'); return;
          }
          setBuyer(prev => ({ ...prev, name: d.name || '', email: d.email || '', phone: d.phone || '' }));
          setProductId(d.productId || productId);
          setQty(d.qty || qty);
          setGuestVerified(true);
          setStep(STEPS.DELIVERY);
          toast.success('Email verified! You can now complete your purchase.');
        }
      };
      loadSession();
    }
  }, [isVerifiedReturn, sessionRef]);

  // ── Computed values ───────────────────────────────────────────────────────
  const displayPrice = Number(product?.displayPrice || 0);
  const basePrice    = Number(product?.basePrice    || 0);
  const platformFee  = Number(product?.platformFee  || 0);
  const itemTotal    = displayPrice * qty;
  const grandTotal   = deliveryConfirmed && deliveryFee !== null
    ? itemTotal + deliveryFee
    : null;

  // ── Delivery confirmation ─────────────────────────────────────────────────
  const handleCheckDelivery = useCallback(() => {
    if (!buyer.address.trim()) { toast.error('Please enter your delivery address first.'); return; }
    const zone = isLagosAddress(buyer.address) ? 'lagos' : 'outside';
    setDeliveryZone(zone);
    setDeliveryFee(zone === 'lagos' ? DELIVERY_LAGOS : DELIVERY_OUTSIDE);
    setShowDeliveryModal(true);
  }, [buyer.address]);

  const handleConfirmDelivery = () => {
    setDeliveryConfirmed(true);
    setShowDeliveryModal(false);
    // If guest and not verified, go to verify; else go to payment
    if (!user && !guestVerified) {
      setStep(STEPS.DETAILS); // stay, will prompt verify
    } else {
      setStep(STEPS.PAYMENT);
    }
  };

  // ── Guest: Send verification email ───────────────────────────────────────
  const handleSendVerificationEmail = async () => {
    if (!buyer.email || !buyer.name || !buyer.phone || !buyer.address) {
      toast.error('Please complete all fields before verifying your email.');
      return;
    }
    setVerifyEmailLoading(true);
    try {
      // Store session
      const sessionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const expiresAt = new Date(Date.now() + 60 * 60 * 1000); // 1 hour
      await setDoc(doc(db, 'guest_checkout_sessions', sessionId), {
        name:       buyer.name.trim(),
        email:      buyer.email.toLowerCase().trim(),
        phone:      buyer.phone.trim(),
        productId:  productId,
        qty:        qty,
        expiresAt:  expiresAt,
        createdAt:  serverTimestamp(),
      });

      // Call cloud function to send verification email
      const sendVerify = httpsCallable(functions, 'merchantVerifyGuestEmail');
      await sendVerify({
        email:     buyer.email.toLowerCase().trim(),
        name:      buyer.name.trim(),
        sessionId: sessionId,
        productId: productId,
        qty:       qty,
      });
      setVerifyEmailSent(true);
      toast.success('Verification email sent! Check your inbox and click the link.');
    } catch (err) {
      toast.error('Failed to send verification email. Please try again.');
      console.error(err);
    } finally {
      setVerifyEmailLoading(false);
    }
  };

  // ── Process payment ───────────────────────────────────────────────────────
  const handleProcessPayment = async () => {
    if (!deliveryConfirmed || deliveryFee === null) {
      toast.error('Please confirm your delivery address first.'); return;
    }
    if (!buyer.name || !buyer.email || !buyer.phone) {
      toast.error('Please complete all buyer details.'); return;
    }

    setLoading(true);
    setLoadingMsg('Initialising payment...');

    try {
      if (paymentMethod === 'monnify') {
        await handleMonnifyPayment();
      } else if (paymentMethod === 'stripe') {
        await handleStripePayment();
      } else if (paymentMethod === 'wallet') {
        await handleWalletPayment();
      }
    } catch (err) {
      toast.error(err.message || 'Payment failed. Please try again.');
      setLoading(false);
      setLoadingMsg('');
    }
  };

  // ── Monnify Payment ───────────────────────────────────────────────────────
  const handleMonnifyPayment = async () => {
    const total = itemTotal + deliveryFee;

    if (!window.MonnifySDK) {
      toast.error('Payment SDK not loaded. Please refresh and try again.');
      setLoading(false); return;
    }

    setLoadingMsg('Opening Monnify payment...');

    window.MonnifySDK.initialize({
      amount:           total,
      currency:         'NGN',
      reference:        `ESDRSMKT_${Date.now()}_${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
      customerFullName: buyer.name,
      customerEmail:    buyer.email,
      customerMobileNumber: buyer.phone,
      apiKey:           process.env.REACT_APP_MONNIFY_API_KEY || 'MK_TEST_placeholder',
      contractCode:     process.env.REACT_APP_MONNIFY_CONTRACT_CODE || '626972837495',
      paymentDescription: `Esdras Marketplace: ${product.name} x${qty}`,
      metadata: {
        productId,
        merchantId,
        qty,
        buyerEmail: buyer.email,
        paymentType: 'MARKETPLACE_PURCHASE',
      },
      onLoadStart:  () => { setLoadingMsg('Loading payment gateway...'); },
      onLoadComplete: () => { setLoading(false); setLoadingMsg(''); },
      onComplete: async (response) => {
        if (response.status === 'SUCCESS') {
          await finaliseOrder(response.paymentReference, 'monnify');
        } else {
          toast.error('Payment was not completed.');
          setLoading(false);
        }
      },
      onClose: () => { setLoading(false); setLoadingMsg(''); },
    });
  };

  // ── Stripe Payment (scaffold — keys to be added) ──────────────────────────
  const handleStripePayment = async () => {
    setLoadingMsg('Preparing Stripe checkout...');
    try {
      const createStripeSession = httpsCallable(functions, 'merchantCreateStripeSession');
      const result = await createStripeSession({
        productId,
        merchantId,
        qty,
        buyerEmail:   buyer.email,
        buyerName:    buyer.name,
        buyerPhone:   buyer.phone,
        deliveryAddress: buyer.address,
        deliveryFee,
        itemTotal,
        grandTotal:   itemTotal + deliveryFee,
        comment:      buyer.comment,
      });
      if (result.data?.url) {
        window.location.href = result.data.url; // Redirect to Stripe Checkout
      } else {
        toast.error('Could not create Stripe session. Please try another payment method.');
        setLoading(false);
      }
    } catch (err) {
      toast.error('Stripe is not yet configured. Please use Monnify for NGN payments.');
      setLoading(false);
    }
  };

  // ── Wallet Payment ────────────────────────────────────────────────────────
  const handleWalletPayment = async () => {
    const total = itemTotal + deliveryFee;
    if (userWalletBal < total) {
      toast.error(`Insufficient wallet balance. You have ₦${userWalletBal.toLocaleString()}, need ₦${total.toLocaleString()}.`);
      setLoading(false); return;
    }
    setLoadingMsg('Processing wallet payment...');
    try {
      // Deduct wallet via cloud function
      const processWallet = httpsCallable(functions, 'merchantWalletPayment');
      const result = await processWallet({
        productId, merchantId, qty,
        buyerUid:  user.uid,
        buyerEmail: buyer.email,
        totalAmount: total,
        deliveryFee,
        comment: buyer.comment,
        deliveryAddress: buyer.address,
      });
      if (result.data?.success) {
        await finaliseOrder(result.data.reference, 'wallet');
      } else {
        toast.error(result.data?.message || 'Wallet payment failed.');
        setLoading(false);
      }
    } catch (err) {
      toast.error(err.message || 'Wallet payment error.');
      setLoading(false);
    }
  };

  // ── Finalise Order (post-payment) ─────────────────────────────────────────
  const finaliseOrder = async (paymentReference, method) => {
    setLoadingMsg('Confirming your order...');
    try {
      const total = itemTotal + deliveryFee;
      const orderId = `ORD_${Date.now()}_${Math.random().toString(36).substr(2, 6).toUpperCase()}`;

      const orderDoc = {
        orderId,
        buyerUid:        user?.uid || null,
        buyerEmail:      buyer.email.toLowerCase().trim(),
        buyerName:       buyer.name.trim(),
        buyerPhone:      buyer.phone.trim(),
        deliveryAddress: buyer.address.trim(),
        comment:         buyer.comment.trim(),
        productId,
        productName:     product.name,
        merchantId,
        merchantName:    product.merchantName || '',
        qty,
        basePrice,
        platformFee,
        displayPrice,
        deliveryFee,
        deliveryZone,
        totalPaid:       total,
        paymentMethod:   method,
        paymentReference,
        paymentStatus:   'paid',
        orderStatus:     'processing',
        actualDeliveryCost: null,
        deliveryNetProfit: null,
        splitStatus:     'pending',
        merchantCredited: false,
        adminCredited:   false,
        createdAt:       serverTimestamp(),
        updatedAt:       serverTimestamp(),
      };

      await setDoc(doc(db, 'merchant_orders', orderId), orderDoc);

      // Log guest buyer data if not logged in
      if (!user) {
        await addDoc(collection(db, 'marketplace_guest_buyers'), {
          name:      buyer.name.trim(),
          email:     buyer.email.toLowerCase().trim(),
          phone:     buyer.phone.trim(),
          address:   buyer.address.trim(),
          orderId,
          productId,
          productName: product.name,
          createdAt: serverTimestamp(),
        });
      }

      // Trigger split payment cloud function
      const splitFn = httpsCallable(functions, 'merchantSplitPayment');
      splitFn({ orderId, paymentReference }).catch(err => console.warn('Split deferred:', err.message));

      // Set receipt data and move to receipt step
      setOrderData({ ...orderDoc, orderId, createdAt: new Date() });
      setStep(STEPS.RECEIPT);
      toast.success('Order confirmed! 🎉');

    } catch (err) {
      console.error('Order finalisation error:', err);
      toast.error('Order recorded but confirmation email may be delayed. Check your inbox.');
      setLoading(false);
    } finally {
      setLoading(false);
      setLoadingMsg('');
    }
  };

  // ── PDF Download ──────────────────────────────────────────────────────────
  const handleDownloadReceipt = () => {
    if (!orderData) return;
    const receiptHTML = generateReceiptHTML(orderData, product, buyer);
    const blob = new Blob([receiptHTML], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `Esdras_Receipt_${orderData.orderId}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Receipt downloaded!');
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────
  if (!product) return (
    <div style={S.page}>
      <div style={S.centered}>
        <div style={S.spinner} />
        <p style={{ color: GOLD, letterSpacing: '2px', fontSize: '0.75rem', marginTop: '16px' }}>
          LOADING CHECKOUT...
        </p>
      </div>
      <style>{cssAnimations}</style>
    </div>
  );

  // ── Step: RECEIPT ─────────────────────────────────────────────────────────
  if (step === STEPS.RECEIPT && orderData) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={S.receiptCard} ref={receiptRef}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <div style={{ fontSize: '3rem', marginBottom: '10px' }}>✅</div>
              <h1 style={{ color: GOLD, letterSpacing: '3px', margin: '0 0 5px', fontSize: '1.5rem' }}>ORDER CONFIRMED</h1>
              <p style={{ color: '#555', fontSize: '0.8rem', letterSpacing: '1px' }}>
                A receipt has been sent to <span style={{ color: GOLD }}>{buyer.email}</span>
              </p>
            </div>

            {/* Order ID */}
            <div style={S.receiptRow}>
              <span style={S.receiptLabel}>ORDER ID</span>
              <span style={{ color: GOLD, fontFamily: 'monospace', fontWeight: '700' }}>{orderData.orderId}</span>
            </div>

            {/* Product */}
            <div style={S.receiptProductRow}>
              <img src={product.imageUrls?.[0] || SAFE_IMG} alt="" style={S.receiptImg}
                onError={e => { e.target.src = SAFE_IMG; }} />
              <div>
                <p style={S.receiptProductName}>{product.name}</p>
                <p style={S.receiptMerchant}>{product.merchantName}</p>
                <p style={{ color: '#555', fontSize: '0.75rem', margin: 0 }}>Qty: {qty}</p>
              </div>
            </div>

            {/* Cost Breakdown */}
            <div style={S.breakdown}>
              {[
                ['Item Total', `₦${(displayPrice * qty).toLocaleString()}`],
                ['Delivery Fee', `₦${deliveryFee?.toLocaleString() || 0}`, deliveryZone === 'outside' ? '#a8dadc' : GOLD],
                ['Grand Total', `₦${orderData.totalPaid.toLocaleString()}`, GOLD, true],
              ].map(([label, val, color, bold]) => (
                <div key={label} style={{ ...S.receiptRow, borderBottom: bold ? 'none' : `1px solid ${BORDER}`, paddingBottom: bold ? 0 : '10px', marginBottom: bold ? 0 : '10px' }}>
                  <span style={{ ...S.receiptLabel, fontWeight: bold ? '900' : '500', color: bold ? 'white' : '#888' }}>{label}</span>
                  <span style={{ color: color || 'white', fontWeight: bold ? '900' : '600', fontSize: bold ? '1.2rem' : '0.9rem' }}>{val}</span>
                </div>
              ))}
            </div>

            {/* Delivery & Contact */}
            <div style={S.receiptInfo}>
              <p style={S.receiptLabel}>DELIVERY TO</p>
              <p style={S.receiptValue}>{buyer.address}</p>
            </div>
            {buyer.comment && (
              <div style={S.receiptInfo}>
                <p style={S.receiptLabel}>NOTE</p>
                <p style={S.receiptValue}>{buyer.comment}</p>
              </div>
            )}

            {/* Order Tracking */}
            <div style={S.trackingBar}>
              {['Paid', 'Processing', 'Dispatched', 'Delivered', 'Received'].map((status, i) => (
                <div key={status} style={S.trackStep}>
                  <div style={{
                    ...S.trackDot,
                    background: i === 0 ? GOLD : '#1a2631',
                    border: `2px solid ${i === 0 ? GOLD : '#2a3641'}`,
                  }}>{i === 0 ? '✓' : ''}</div>
                  <span style={{ color: i === 0 ? GOLD : '#444', fontSize: '0.62rem', marginTop: '4px' }}>{status}</span>
                </div>
              ))}
            </div>

            {/* Payment Method */}
            <div style={S.receiptInfo}>
              <p style={S.receiptLabel}>PAID VIA</p>
              <p style={S.receiptValue}>{orderData.paymentMethod?.toUpperCase()} — {orderData.paymentReference}</p>
            </div>

            {/* Download */}
            <button onClick={handleDownloadReceipt} style={S.outlineBtn}>
              ⬇ DOWNLOAD RECEIPT
            </button>
            <button onClick={() => navigate('/')} style={{ ...S.goldBtn, marginTop: '10px' }}>
              ← BACK TO ESDRAS
            </button>
          </div>
        </div>
        <style>{cssAnimations}</style>
      </div>
    );
  }

  // ── Steps: DETAILS / DELIVERY / PAYMENT ──────────────────────────────────
  return (
    <div style={S.page}>
      {/* Delivery Confirmation Modal */}
      {showDeliveryModal && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🚚</div>
            <h3 style={{ color: GOLD, letterSpacing: '2px', margin: '0 0 10px' }}>CONFIRM DELIVERY</h3>
            <p style={{ color: '#bbb', fontSize: '0.88rem', lineHeight: '1.6', margin: '0 0 16px' }}>
              Delivering to:<br />
              <span style={{ color: 'white', fontWeight: '600' }}>{buyer.address}</span>
            </p>
            <div style={S.deliveryZoneBox}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#ccc' }}>
                {deliveryZone === 'lagos' ? '📍 Lagos State' : '📍 Outside Lagos'}
              </p>
              <p style={{
                margin: '6px 0 0', fontSize: '1.5rem', fontWeight: '900',
                color: deliveryZone === 'lagos' ? GOLD : '#a8dadc',
              }}>
                ₦{deliveryFee?.toLocaleString()} delivery fee
              </p>
              <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: '#555' }}>
                Flat rate per order — not per item
              </p>
            </div>
            <button onClick={handleConfirmDelivery} style={S.goldBtn}>
              CONFIRM & CONTINUE
            </button>
            <button onClick={() => setShowDeliveryModal(false)} style={S.ghostBtn}>
              CHANGE ADDRESS
            </button>
          </div>
        </div>
      )}

      <div style={S.container}>
        {/* Breadcrumb */}
        <div style={S.breadcrumb}>
          <button onClick={() => navigate(`/marketplace/product/${productId}`)} style={S.bcLink}>← Product</button>
          <span style={{ color: '#333' }}> / </span>
          <span style={{ color: GOLD, fontSize: '0.78rem' }}>Checkout</span>
        </div>

        <h1 style={S.pageTitle}>CHECKOUT</h1>

        <div style={S.twoCol}>
          {/* ── LEFT: Buyer Form ── */}
          <div style={S.formCol}>

            {/* STEP INDICATOR */}
            <div style={S.stepRow}>
              {['Details', 'Delivery', 'Payment'].map((label, i) => (
                <React.Fragment key={label}>
                  <div style={S.stepItem}>
                    <div style={{
                      ...S.stepCircle,
                      background: step >= i ? GOLD : '#1a2631',
                      color: step >= i ? 'black' : '#555',
                    }}>{i + 1}</div>
                    <span style={{ color: step === i ? GOLD : '#555', fontSize: '0.7rem', fontWeight: step === i ? '900' : '500' }}>
                      {label}
                    </span>
                  </div>
                  {i < 2 && <div style={{ flex: 1, height: '1px', background: step > i ? GOLD : '#1a2631', margin: '0 8px', alignSelf: 'flex-start', marginTop: '14px' }} />}
                </React.Fragment>
              ))}
            </div>

            {/* ── STEP 0: DETAILS ── */}
            {step === STEPS.DETAILS && (
              <div style={S.card}>
                <SectionLabel>YOUR DETAILS</SectionLabel>
                {user ? (
                  <p style={{ color: '#4caf50', fontSize: '0.78rem', margin: '0 0 14px' }}>
                    ✅ Signed in as <strong>{buyer.email}</strong>
                  </p>
                ) : (
                  <p style={{ color: '#888', fontSize: '0.78rem', margin: '0 0 14px', lineHeight: '1.5' }}>
                    You're checking out as a guest. We'll need to verify your email before payment.
                  </p>
                )}
                <input placeholder="Full Name" value={buyer.name}
                  onChange={e => setBuyer(p => ({ ...p, name: e.target.value }))} style={S.input} required />
                <input type="email" placeholder="Email Address" value={buyer.email}
                  onChange={e => setBuyer(p => ({ ...p, email: e.target.value }))} style={S.input} required
                  readOnly={!!user} />
                <input type="tel" placeholder="Phone Number" value={buyer.phone}
                  onChange={e => setBuyer(p => ({ ...p, phone: e.target.value }))} style={S.input} required />

                <SectionLabel style={{ marginTop: '16px' }}>DELIVERY ADDRESS</SectionLabel>
                <textarea placeholder="Full delivery address (include area, city)" value={buyer.address}
                  onChange={e => setBuyer(p => ({ ...p, address: e.target.value }))}
                  style={{ ...S.input, minHeight: '80px', resize: 'vertical' }} required />

                <SectionLabel style={{ marginTop: '14px' }}>NOTE FOR SELLER (OPTIONAL)</SectionLabel>
                <textarea placeholder="Any special instructions, questions, or notes for the seller / delivery team..."
                  value={buyer.comment}
                  onChange={e => setBuyer(p => ({ ...p, comment: e.target.value }))}
                  style={{ ...S.input, minHeight: '70px', resize: 'vertical' }} />

                {/* Guest: verify email before continuing */}
                {!user && !guestVerified && !verifyEmailSent && (
                  <div style={S.verifyBox}>
                    <p style={{ color: '#aaa', fontSize: '0.82rem', margin: '0 0 12px', lineHeight: '1.5' }}>
                      📧 Before proceeding to payment, we need to verify your email address.
                      A link will be sent to <strong style={{ color: GOLD }}>{buyer.email || 'your email'}</strong> — click it to continue your checkout.
                    </p>
                    <button onClick={handleSendVerificationEmail}
                      disabled={verifyEmailLoading || !buyer.email || !buyer.name || !buyer.phone || !buyer.address}
                      style={{ ...S.goldBtn, opacity: (!buyer.email || !buyer.name || !buyer.phone || !buyer.address) ? 0.5 : 1 }}>
                      {verifyEmailLoading ? 'SENDING...' : 'VERIFY MY EMAIL & CONTINUE'}
                    </button>
                  </div>
                )}

                {!user && !guestVerified && verifyEmailSent && (
                  <div style={{ ...S.verifyBox, background: 'rgba(76,175,80,0.08)', borderColor: '#4caf5044' }}>
                    <p style={{ color: '#4caf50', fontSize: '0.85rem', margin: 0, lineHeight: '1.6' }}>
                      ✅ Verification email sent to <strong>{buyer.email}</strong>.<br />
                      Click the link in your inbox to continue. This page will stay active.<br />
                      <span style={{ color: '#888', fontSize: '0.75rem' }}>Link expires in 1 hour.</span>
                    </p>
                    <button onClick={handleSendVerificationEmail}
                      style={{ background: 'transparent', border: `1px solid #4caf5044`, color: '#4caf50', padding: '8px 16px', borderRadius: '50px', cursor: 'pointer', fontSize: '0.75rem', marginTop: '10px', fontFamily: 'inherit' }}>
                      Resend Link
                    </button>
                  </div>
                )}

                {/* Continue button for logged-in or already verified guests */}
                {(user || guestVerified) && (
                  <button onClick={() => {
                    if (!buyer.name || !buyer.email || !buyer.phone || !buyer.address) {
                      toast.error('Please fill in all required fields.'); return;
                    }
                    handleCheckDelivery();
                  }} style={S.goldBtn}>
                    CONFIRM DELIVERY ADDRESS →
                  </button>
                )}
              </div>
            )}

            {/* ── STEP 1: DELIVERY CONFIRMED → PAYMENT ── */}
            {(step === STEPS.DELIVERY || step === STEPS.PAYMENT) && deliveryConfirmed && (
              <div style={S.card}>
                {/* Confirmed delivery info */}
                <div style={S.confirmedDelivery}>
                  <span style={{ color: '#4caf50', fontSize: '0.82rem' }}>✅ Delivery confirmed</span>
                  <span style={{ color: GOLD, fontWeight: '900' }}>₦{deliveryFee?.toLocaleString()}</span>
                  <button onClick={() => { setDeliveryConfirmed(false); setStep(STEPS.DETAILS); }}
                    style={{ background: 'transparent', border: 'none', color: '#555', cursor: 'pointer', fontSize: '0.72rem', textDecoration: 'underline', fontFamily: 'inherit' }}>
                    Change
                  </button>
                </div>

                <SectionLabel style={{ marginTop: '20px' }}>PAYMENT METHOD</SectionLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[
                    { id: 'monnify', icon: '🏦', label: 'Pay with Monnify', sub: 'Bank transfer, card, USSD — NGN only' },
                    { id: 'stripe',  icon: '💳', label: 'Pay with Stripe',  sub: 'International cards — USD / other currencies' },
                    ...(user ? [{ id: 'wallet', icon: '💰', label: 'Pay with Esdras Wallet', sub: `Balance: ₦${userWalletBal.toLocaleString()}` }] : []),
                  ].map(pm => (
                    <div key={pm.id} onClick={() => setPaymentMethod(pm.id)}
                      style={{
                        ...S.pmOption,
                        border: `1.5px solid ${paymentMethod === pm.id ? GOLD : BORDER}`,
                        background: paymentMethod === pm.id ? `${GOLD}0a` : CARD_BG,
                      }}>
                      <div style={{ ...S.pmRadio, borderColor: paymentMethod === pm.id ? GOLD : '#2a3641', background: paymentMethod === pm.id ? GOLD : 'transparent' }} />
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: '0 0 2px', color: 'white', fontWeight: '700', fontSize: '0.88rem' }}>
                          {pm.icon} {pm.label}
                        </p>
                        <p style={{ margin: 0, color: '#555', fontSize: '0.72rem' }}>{pm.sub}</p>
                        {pm.id === 'wallet' && userWalletBal < itemTotal + (deliveryFee || 0) && (
                          <p style={{ margin: '3px 0 0', color: '#ff4d4d', fontSize: '0.7rem' }}>Insufficient balance</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <button onClick={handleProcessPayment}
                  disabled={loading || (paymentMethod === 'wallet' && userWalletBal < itemTotal + (deliveryFee || 0))}
                  style={{ ...S.goldBtn, marginTop: '20px', opacity: loading ? 0.7 : 1 }}>
                  {loading ? loadingMsg || 'PROCESSING...' : `PAY ₦${(itemTotal + (deliveryFee || 0)).toLocaleString()}`}
                </button>
              </div>
            )}
          </div>

          {/* ── RIGHT: Order Summary ── */}
          <div style={S.summaryCol}>
            <div style={S.summaryCard}>
              <p style={S.summaryTitle}>ORDER SUMMARY</p>
              {/* Product */}
              <div style={S.summaryProduct}>
                <img src={product.imageUrls?.[0] || SAFE_IMG} alt="" style={S.summaryImg}
                  onError={e => { e.target.src = SAFE_IMG; }} />
                <div style={{ flex: 1 }}>
                  <p style={S.summaryProductName}>{product.name}</p>
                  <p style={S.summaryMerchant}>{product.merchantName}</p>
                  <p style={{ color: '#555', fontSize: '0.72rem', margin: '4px 0 0' }}>Qty: {qty}</p>
                </div>
                <p style={S.summaryItemPrice}>₦{(displayPrice * qty).toLocaleString()}</p>
              </div>

              {/* Cost Lines */}
              <div style={S.summaryLines}>
                <div style={S.summaryLine}>
                  <span style={S.summaryLineLabel}>Item Price</span>
                  <span style={S.summaryLineVal}>₦{displayPrice.toLocaleString()} × {qty}</span>
                </div>
                <div style={S.summaryLine}>
                  <span style={S.summaryLineLabel}>Item Total</span>
                  <span style={S.summaryLineVal}>₦{itemTotal.toLocaleString()}</span>
                </div>
                <div style={S.summaryLine}>
                  <span style={S.summaryLineLabel}>Delivery</span>
                  <span style={{ color: deliveryConfirmed ? GOLD : '#555', fontSize: '0.82rem' }}>
                    {deliveryConfirmed ? `₦${deliveryFee?.toLocaleString()}` : 'Enter address'}
                  </span>
                </div>
              </div>

              <div style={S.summaryTotal}>
                <span style={{ color: 'white', fontWeight: '900', fontSize: '0.9rem' }}>TOTAL</span>
                <span style={{ color: GOLD, fontWeight: '900', fontSize: '1.3rem' }}>
                  {deliveryConfirmed
                    ? `₦${(itemTotal + deliveryFee).toLocaleString()}`
                    : `₦${itemTotal.toLocaleString()} + delivery`}
                </span>
              </div>
            </div>

            {/* Trust Badges */}
            <div style={S.trustBox}>
              {['🔒 Secure Payment', '📦 Tracked Delivery', '✅ Verified Merchant'].map(b => (
                <p key={b} style={{ color: '#444', fontSize: '0.72rem', margin: '4px 0' }}>{b}</p>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{cssAnimations}</style>
    </div>
  );
}

// ─── RECEIPT HTML GENERATOR ───────────────────────────────────────────────────
function generateReceiptHTML(order, product, buyer) {
  const date = new Date().toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Esdras Marketplace Receipt — ${order.orderId}</title>
  <style>
    body { background: #050a0f; color: white; font-family: 'Helvetica Neue', sans-serif; padding: 40px; max-width: 600px; margin: auto; }
    .gold { color: #B8860B; } .muted { color: #888; font-size: 0.82rem; }
    .card { background: #0a0f14; border: 1px solid #1a2631; border-radius: 16px; padding: 24px; margin: 16px 0; }
    .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #1a2631; }
    .total-row { display: flex; justify-content: space-between; padding: 12px 0; }
    h1 { color: #B8860B; letter-spacing: 3px; }
    .status { background: #B8860B22; border: 1px solid #B8860B44; padding: 8px 16px; border-radius: 8px; color: #B8860B; font-size: 0.8rem; font-weight: bold; display: inline-block; }
  </style>
</head>
<body>
  <div style="text-align:center; margin-bottom:30px;">
    <h1>ESDRAS MARKETPLACE</h1>
    <div class="status">✅ ORDER CONFIRMED</div>
    <p class="muted" style="margin-top:10px;">${date}</p>
  </div>
  <div class="card">
    <div class="row"><span class="muted">Order ID</span><span class="gold" style="font-family:monospace;">${order.orderId}</span></div>
    <div class="row"><span class="muted">Payment Ref</span><span style="font-family:monospace;font-size:0.8rem;">${order.paymentReference}</span></div>
    <div class="row"><span class="muted">Payment Method</span><span>${order.paymentMethod?.toUpperCase()}</span></div>
  </div>
  <div class="card">
    <p class="gold" style="font-size:0.65rem;letter-spacing:2px;font-weight:900;margin:0 0 12px;">PRODUCT</p>
    <div class="row"><span class="muted">Item</span><span>${product?.name}</span></div>
    <div class="row"><span class="muted">Merchant</span><span>${product?.merchantName}</span></div>
    <div class="row"><span class="muted">Quantity</span><span>${order.qty}</span></div>
    <div class="row"><span class="muted">Unit Price</span><span>₦${Number(order.displayPrice).toLocaleString()}</span></div>
    <div class="row"><span class="muted">Item Total</span><span>₦${(Number(order.displayPrice) * order.qty).toLocaleString()}</span></div>
    <div class="row"><span class="muted">Delivery Fee</span><span class="gold">₦${Number(order.deliveryFee).toLocaleString()}</span></div>
    <div class="total-row"><span style="font-weight:900;">TOTAL PAID</span><span class="gold" style="font-size:1.3rem;font-weight:900;">₦${Number(order.totalPaid).toLocaleString()}</span></div>
  </div>
  <div class="card">
    <p class="gold" style="font-size:0.65rem;letter-spacing:2px;font-weight:900;margin:0 0 12px;">DELIVERY DETAILS</p>
    <div class="row"><span class="muted">Name</span><span>${buyer.name}</span></div>
    <div class="row"><span class="muted">Email</span><span>${buyer.email}</span></div>
    <div class="row"><span class="muted">Phone</span><span>${buyer.phone}</span></div>
    <div class="row"><span class="muted">Address</span><span>${buyer.address}</span></div>
    ${buyer.comment ? `<div class="row"><span class="muted">Note</span><span>${buyer.comment}</span></div>` : ''}
  </div>
  <p class="muted" style="text-align:center;margin-top:30px;">
    Questions? Contact <a href="mailto:esdrastechnologiesinc@gmail.com" style="color:#B8860B;">esdrastechnologiesinc@gmail.com</a>
    <br/>© ${new Date().getFullYear()} Esdras Technologies Inc.
  </p>
</body>
</html>`;
}

// ─── SUB-COMPONENTS ──────────────────────────────────────────────────────────
function SectionLabel({ children, style }) {
  return (
    <p style={{ color: GOLD, fontSize: '0.62rem', letterSpacing: '2px', fontWeight: '900', margin: '0 0 8px', ...style }}>
      {children}
    </p>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const S = {
  page:    { minHeight: '100vh', background: DEEP_BLACK, color: 'white', fontFamily: "'Montserrat', 'Inter', sans-serif" },
  centered:{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  container: { maxWidth: '1000px', margin: '0 auto', padding: '30px 24px 60px' },
  pageTitle: { color: GOLD, fontWeight: '900', letterSpacing: '4px', margin: '0 0 28px', fontSize: '1.4rem' },
  breadcrumb: { marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '4px' },
  bcLink: { background: 'transparent', border: 'none', color: '#555', cursor: 'pointer', fontSize: '0.78rem', padding: 0, fontFamily: 'inherit' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 380px', gap: '24px', alignItems: 'start' },
  formCol: { display: 'flex', flexDirection: 'column', gap: '16px' },
  summaryCol: { display: 'flex', flexDirection: 'column', gap: '12px', position: 'sticky', top: '20px' },
  card: { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '16px', padding: '22px', display: 'flex', flexDirection: 'column', gap: '12px' },
  input: { background: '#060c11', border: `1px solid ${BORDER}`, padding: '13px 15px', borderRadius: '10px', color: 'white', fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  verifyBox: { background: 'rgba(184,134,11,0.05)', border: `1px solid ${GOLD}33`, borderRadius: '12px', padding: '16px' },
  confirmedDelivery: { display: 'flex', alignItems: 'center', gap: '12px', background: '#060c11', border: `1px solid #1a2631`, borderRadius: '10px', padding: '10px 14px', fontSize: '0.82rem' },
  stepRow: { display: 'flex', alignItems: 'flex-start', marginBottom: '4px' },
  stepItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
  stepCircle: { width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '0.78rem' },
  pmOption: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s' },
  pmRadio: { width: '18px', height: '18px', borderRadius: '50%', border: '2px solid', flexShrink: 0 },
  goldBtn: { background: GOLD, color: 'black', padding: '16px', borderRadius: '50px', fontWeight: '900', border: 'none', cursor: 'pointer', fontSize: '0.88rem', letterSpacing: '1px', width: '100%', fontFamily: 'inherit' },
  outlineBtn: { background: 'transparent', color: GOLD, padding: '14px', borderRadius: '50px', fontWeight: '900', border: `2px solid ${GOLD}`, cursor: 'pointer', fontSize: '0.88rem', letterSpacing: '1px', width: '100%', fontFamily: 'inherit' },
  ghostBtn: { background: 'transparent', color: '#555', padding: '12px', borderRadius: '50px', fontWeight: '600', border: '1px solid #1a2631', cursor: 'pointer', fontSize: '0.82rem', width: '100%', fontFamily: 'inherit', marginTop: '8px' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal: { background: DEEP_BLACK, border: `1px solid ${GOLD}33`, borderRadius: '20px', padding: '32px 28px', width: '100%', maxWidth: '380px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '12px' },
  deliveryZoneBox: { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '16px', textAlign: 'center' },
  summaryCard: { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '16px', padding: '22px' },
  summaryTitle: { color: GOLD, fontWeight: '900', fontSize: '0.72rem', letterSpacing: '2px', margin: '0 0 16px' },
  summaryProduct: { display: 'flex', gap: '12px', marginBottom: '16px', paddingBottom: '16px', borderBottom: `1px solid ${BORDER}` },
  summaryImg: { width: '60px', height: '60px', borderRadius: '10px', objectFit: 'cover', border: `1px solid ${BORDER}` },
  summaryProductName: { color: 'white', fontWeight: '700', fontSize: '0.85rem', margin: '0 0 3px' },
  summaryMerchant: { color: '#555', fontSize: '0.7rem', margin: 0 },
  summaryItemPrice: { color: GOLD, fontWeight: '900', fontSize: '0.95rem', whiteSpace: 'nowrap' },
  summaryLines: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' },
  summaryLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  summaryLineLabel: { color: '#555', fontSize: '0.78rem' },
  summaryLineVal: { color: '#ccc', fontSize: '0.82rem' },
  summaryTotal: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '14px', borderTop: `1px solid ${BORDER}` },
  trustBox: { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '14px', padding: '14px' },
  receiptCard: { background: CARD_BG, border: `1px solid ${GOLD}33`, borderRadius: '20px', padding: '32px', maxWidth: '540px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  receiptRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  receiptLabel: { color: '#555', fontSize: '0.68rem', letterSpacing: '1.5px', fontWeight: '700', margin: 0 },
  receiptValue: { color: '#ccc', fontSize: '0.85rem', margin: '3px 0 0' },
  receiptProductRow: { display: 'flex', gap: '14px', alignItems: 'center', padding: '14px 0', borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` },
  receiptImg: { width: '60px', height: '60px', borderRadius: '10px', objectFit: 'cover' },
  receiptProductName: { color: 'white', fontWeight: '700', fontSize: '0.92rem', margin: '0 0 3px' },
  receiptMerchant: { color: '#555', fontSize: '0.72rem', margin: 0 },
  breakdown: { display: 'flex', flexDirection: 'column', background: '#060c11', borderRadius: '12px', padding: '16px', gap: '0' },
  receiptInfo: { background: '#060c11', borderRadius: '10px', padding: '12px 14px' },
  trackingBar: { display: 'flex', justifyContent: 'space-between', padding: '16px', background: '#060c11', borderRadius: '12px' },
  trackStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 },
  trackDot: { width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: '900', color: 'black' },
  spinner: { width: '40px', height: '40px', border: `3px solid ${GOLD}22`, borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite' },
};

const cssAnimations = `@keyframes spin { 100% { transform: rotate(360deg); } }`;
